/* eslint-disable jsx-a11y/anchor-is-valid */
import ApexCharts, { ApexOptions } from 'apexcharts'
import React, { useEffect, useRef, useState } from 'react'
import { getCSS, getCSSVariableValue } from '../../../_metronic/assets/ts/_utils'
import { useThemeMode } from '../../../_metronic/partials'
import { formatNumberToBRL } from '../../modules/utils/currency'
import { getOrderMonthlyReport } from '../../services/Reports'
// import ptBR from 'rsuite/locales/pt_BR'
import DateRangePerMonth from '../../../_metronic/layout/components/DateRangePerMonth'

type Props = {
    className: string
}


const MONTH_NAME_MAP: Record<string, string> = {
    '01': 'Jan',
    '02': 'Fev',
    '03': 'Mar',
    '04': 'Abr',
    '05': 'Mai',
    '06': 'Jun',
    '07': 'Jul',
    '08': 'Ago',
    '09': 'Set',
    '10': 'Out',
    '11': 'Nov',
    '12': 'Dez',
}


const ReportsChart: React.FC<Props> = ({ className }) => {
    const [periodFiler, setPeriodFilter] = useState<{
        startDate: Date
        endDate: Date
    }>({
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        endDate: new Date()
    })

    const chartRef = useRef<HTMLDivElement | null>(null)
    const { mode } = useThemeMode()

    const refreshChart = (startDate?: Date, endDate?: Date) => {
        if (!chartRef.current) {
            return
        }

        const height = parseInt(getCSS(chartRef.current, 'height'))

        const chart = new ApexCharts(chartRef.current, getChartOptions(height))
        if (chart) {
            chart.render()
            getOrderMonthlyReport(startDate, endDate).then((data) => {
                const totalPv = data.map((item) => item.n_total_pv)
                const totalPo = data.map((item) => item.n_total_po)
                const months = data.map((item) => {
                    const [year, month] = item.s_mes_ano_referencia.split('-')
                    const yearFinal = year.slice(2)
                    return [yearFinal, month]
                }).map(([year, month]) => `${MONTH_NAME_MAP[month]}/${year}`)
                chart.updateSeries([
                    {
                        name: 'Total PV',
                        data: totalPv,
                    },
                    {
                        name: 'Total PO',
                        data: totalPo,
                    },
                ])
                chart.updateOptions({
                    xaxis: {
                        categories: months,
                    },
                })
            })
        }

        return chart
    }

    useEffect(() => {
        const chart = refreshChart(periodFiler.startDate, periodFiler.endDate)

        return () => {
            if (chart) {
                chart.destroy()
            }
        }
    }, [chartRef, mode, periodFiler])

    return (
        <div className={`card ${className}`}>
            {/* begin::Header */}
            <div className='card-header border-0 pt-5'>
                {/* begin::Title */}
                <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold fs-3 mb-1'>Relatório mensal</span>
                    <span className='text-muted fw-semibold fs-7'>Valor de Orçamentos e Pedidos de Venda por mês</span>
                </h3>
                {/* end::Title */}

                {/* begin::Toolbar */}
                <div className='card-toolbar'>
                    {/* begin::Menu */}

                    <DateRangePerMonth dateState={periodFiler} onDateSelect={setPeriodFilter} />
                    {/* end::Menu */}
                </div>
                {/* end::Toolbar */}
            </div>
            {/* end::Header */}

            {/* begin::Body */}
            <div className='card-body'>
                {/* begin::Chart */}
                <div ref={chartRef} id='kt_charts_widget_1_chart' style={{ height: '350px' }} />
                {/* end::Chart */}
            </div>
            {/* end::Body */}
        </div>
    )
}

export { ReportsChart }

function getChartOptions(height: number): ApexOptions {
    const labelColor = getCSSVariableValue('--bs-gray-500')
    const borderColor = getCSSVariableValue('--bs-gray-200')
    const baseColor = getCSSVariableValue('--bs-primary')
    const secondaryColor = getCSSVariableValue('--bs-gray-300')



    return {
        noData: {
            text: 'Nenhum dado para esse período'
        },
        series: [
            {
                name: 'Total PV',
                data: [0, 0, 0, 0, 0, 0],
            },
            {
                name: 'Total PO',
                data: [0, 0, 0, 0, 0, 0],
            },
        ],
        chart: {
            fontFamily: 'inherit',
            type: 'bar',
            height: height,
            toolbar: {
                show: false,
            },
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '30%',
                borderRadius: 5,
            },
        },
        legend: {
            show: false,
        },
        dataLabels: {
            enabled: false,
        },
        stroke: {
            show: true,
            width: 2,
            colors: ['transparent'],
        },
        xaxis: {
            categories: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
            labels: {
                style: {
                    colors: labelColor,
                    fontSize: '12px',
                },

            },
        },
        yaxis: {
            labels: {
                style: {
                    colors: labelColor,
                    fontSize: '12px',
                },
                formatter: function (val) {
                    return formatNumberToBRL(val)
                },
            },
        },
        fill: {
            opacity: 1,
        },
        states: {
            normal: {
                filter: {
                    type: 'none',
                    value: 0,
                },
            },
            hover: {
                filter: {
                    type: 'none',
                    value: 0,
                },
            },
            active: {
                allowMultipleDataPointsSelection: false,
                filter: {
                    type: 'none',
                    value: 0,
                },
            },
        },
        tooltip: {
            style: {
                fontSize: '12px',
            },
            y: {
                formatter: function (val) {
                    return formatNumberToBRL(val)
                },
            },
        },
        colors: [baseColor, secondaryColor],
        grid: {
            borderColor: borderColor,
            strokeDashArray: 4,
            yaxis: {
                lines: {
                    show: true,
                },
            },
        },
    }
}
